import { ExtendDirection } from '@/modules/common/types/extend-direction';
import { formatDateRange } from '@/utils/date-formatters';

import {
  ChangeDeletionConflictData,
  ChangeExtensionOption,
} from '../../types/project-change-deletion';
import { ProjectChangeLogEntry } from '../../types/project-changes';

/**
 * Finds the previous change of the same type
 * Changes array must be sorted by startDate descending (newest first)
 */
export const findPreviousChange = (
  changes: ProjectChangeLogEntry[],
  currentChange: ProjectChangeLogEntry
): ProjectChangeLogEntry | null => {
  const currentIndex = changes.findIndex(c => c.id === currentChange.id);
  if (currentIndex === -1) return null;

  // Look forward in the array (earlier dates) for same type
  for (let i = currentIndex + 1; i < changes.length; i++) {
    if (changes[i].type === currentChange.type) {
      return changes[i];
    }
  }
  return null;
};

/**
 * Finds the next change of the same type
 * Changes array must be sorted by startDate descending (newest first)
 */
export const findNextChange = (
  changes: ProjectChangeLogEntry[],
  currentChange: ProjectChangeLogEntry
): ProjectChangeLogEntry | null => {
  const currentIndex = changes.findIndex(c => c.id === currentChange.id);
  if (currentIndex === -1) return null;

  // Look backward in the array (later dates) for same type
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (changes[i].type === currentChange.type) {
      return changes[i];
    }
  }
  return null;
};

/**
 * Calculates what the date range would be if previous change is extended
 */
const calculateExtendPreviousRange = (
  previousChange: ProjectChangeLogEntry,
  deletedChange: ProjectChangeLogEntry
): { startDate: string; endDate: string | null } => {
  return {
    startDate: previousChange.startDate,
    endDate: deletedChange.endDate,
  };
};

/**
 * Calculates what the date range would be if next change is shifted
 */
const calculateShiftNextRange = (
  nextChange: ProjectChangeLogEntry,
  deletedChange: ProjectChangeLogEntry
): { startDate: string; endDate: string | null } => {
  return {
    startDate: deletedChange.startDate,
    endDate: nextChange.endDate,
  };
};

/**
 * Builds the available extension options based on adjacent changes
 */
export const buildExtensionOptions = (
  t: (key: string) => string,
  previousChange: ProjectChangeLogEntry | null,
  nextChange: ProjectChangeLogEntry | null,
  deletedChange: ProjectChangeLogEntry
): ChangeExtensionOption[] => {
  const options: ChangeExtensionOption[] = [];

  if (previousChange) {
    const range = calculateExtendPreviousRange(previousChange, deletedChange);
    options.push({
      direction: ExtendDirection.Previous,
      label: t('authenticated.projects:details.tabs.changes.deleteModal.extendPrevious'),
      dateRange: formatDateRange(range.startDate, range.endDate),
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  if (nextChange) {
    const range = calculateShiftNextRange(nextChange, deletedChange);
    options.push({
      direction: ExtendDirection.Next,
      label: t('authenticated.projects:details.tabs.changes.deleteModal.shiftNext'),
      dateRange: formatDateRange(range.startDate, range.endDate),
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  return options;
};

/**
 * Analyzes deletion context and determines if modal is needed
 * Returns null if auto-delete (only one option), otherwise returns conflict data
 */
export const analyzeChangeDeletion = (
  changes: ProjectChangeLogEntry[],
  changeToDelete: ProjectChangeLogEntry,
  t: (key: string) => string
): ChangeDeletionConflictData | null => {
  const previousChange = findPreviousChange(changes, changeToDelete);
  const nextChange = findNextChange(changes, changeToDelete);

  // Build options based on what's available
  const availableOptions = buildExtensionOptions(t, previousChange, nextChange, changeToDelete);

  // If no options or only one option, return null (auto-delete or error)
  if (availableOptions.length <= 1) {
    return null;
  }

  // Both options available - show modal
  return {
    changeToDelete,
    previousChange,
    nextChange,
    availableOptions,
  };
};
